import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { AccountContact } from '@/api/commercial/accounts';
import { type CreditAnalysis, creditAnalysisApi } from '@/api/commercial/credit-analysis';
import {
  type CreditAnalysisDocumentReview,
  type CreditAnalysisDocumentReviewStatus,
  creditAnalysisDocumentReviewApi,
} from '@/api/commercial/credit-analysis-document-reviews';
import { type AccountUpload, accountUploadApi } from '@/api/upload/account-uploads';
import type { AccountType } from '@/hooks/commercial/use-credit-analysis-state-machine';
import { fieldsByAccountType } from '@/hooks/commercial/use-credit-analysis-state-machine';
import { useToast } from '@/hooks/use-toast';
import { fetchWithAuth } from '@/lib/api';

interface UseDocumentUploadTabAnalysisParams {
  creditAnalysis: CreditAnalysis | null;
  proposalId: string;
  account: { id: string; accountType?: string } | null;
  accountType: AccountType;
  contacts?: AccountContact[];
}

export interface DocumentField {
  key: string;
  label: string;
  required: boolean;
  accept?: string;
}

const getFileExtension = (fileName: string) => {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts.pop() : '';
};

export function useDocumentUploadTabAnalysis({
  creditAnalysis,
  proposalId,
  account,
  accountType,
}: UseDocumentUploadTabAnalysisParams) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [isUploading, setIsUploading] = useState(false);

  const [previewFile, setPreviewFile] = useState<{ url: string; name: string } | null>(null);

  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [approvalDialog, setApprovalDialog] = useState<{
    isOpen: boolean;
    fieldKey: string;
    fieldLabel: string;
    reviewId: string;
  }>({
    isOpen: false,
    fieldKey: '',
    fieldLabel: '',
    reviewId: '',
  });
  const [rejectionDialog, setRejectionDialog] = useState<{
    isOpen: boolean;
    fieldKey: string;
    fieldLabel: string;
    reviewId: string;
    rejectionReason: string;
  }>({
    isOpen: false,
    fieldKey: '',
    fieldLabel: '',
    reviewId: '',
    rejectionReason: '',
  });

  const currentFields: DocumentField[] = (fieldsByAccountType[accountType] || []).filter(
    (field) => field.key !== 'partner_director_doc' && !field.key.startsWith('complementary_'),
  );

  const { data: accountUploadsResponse } = useQuery({
    queryKey: ['account-uploads', account?.id, 'credit-analysis'],
    queryFn: () => accountUploadApi.list(String(account?.id), 'credit-analysis'),
    enabled: !!account?.id,
  });

  const accountUploads = accountUploadsResponse?.docs ?? [];

  const { data: documentReviews = [] } = useQuery({
    queryKey: ['credit-analysis-document-reviews', creditAnalysis?.id],
    queryFn: async () => {
      if (!creditAnalysis?.id || !account?.id) {
        return [];
      }
      const reviews = await creditAnalysisDocumentReviewApi.sync({
        creditAnalysisId: creditAnalysis.id,
        accountId: account.id,
      });
      return reviews;
    },
    enabled: !!creditAnalysis?.id && !!account?.id,
    staleTime: 60 * 1000,
  });

  const documentReviewsMap = new Map<string, CreditAnalysisDocumentReview>();
  const reviewsArray = Array.isArray(documentReviews) ? documentReviews : [];
  reviewsArray.forEach((review) => {
    const upload = accountUploads.find((u) => u.id === review.accountUploadId);
    if (upload?.fieldId) {
      documentReviewsMap.set(upload.fieldId, review);
    }
  });

  const documentFilesMap = new Map<string, AccountUpload>();
  accountUploads.forEach((upload) => {
    if (upload.fieldId) {
      documentFilesMap.set(upload.fieldId, upload);
    }
  });

  const uploadDocumentsMutation = useMutation({
    mutationFn: async () => {
      if (!account?.id) throw new Error('Account ID is required');
      if (!creditAnalysis?.id)
        throw new Error('Credit Analysis ID is required for document upload');

      const filesToUpload: Array<{
        fieldId: string;
        file: File;
        fileName: string;
        contentType: string;
      }> = [];

      Object.entries(files).forEach(([key, file]) => {
        if (file) {
          filesToUpload.push({
            fieldId: key,
            file,
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
          });
        }
      });

      const uploadPromises = filesToUpload.map(async (fileInfo) => {
        setUploadProgress((prev) => ({ ...prev, [fileInfo.fieldId]: 10 }));

        const response = await fetchWithAuth('/api/upload/s3/sign', {
          method: 'POST',
          body: JSON.stringify({
            key: `${account.id}/uploads/credit-analysis/${fileInfo.fieldId}.${getFileExtension(fileInfo.fileName)}`,
            contentType: fileInfo.contentType,
          }),
        });

        if (!response.ok) throw new Error(`Failed to get signed URL for ${fileInfo.fileName}`);

        const responseData = await response.json();
        const { uploadUrl, key } = responseData.data || responseData;
        setUploadProgress((prev) => ({ ...prev, [fileInfo.fieldId]: 30 }));

        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: fileInfo.file,
          headers: { 'Content-Type': fileInfo.contentType },
        });

        if (!uploadResponse.ok) {
          throw new Error(
            `Failed to upload ${fileInfo.fileName} to S3 (Status: ${uploadResponse.status})`,
          );
        }

        const eTag = uploadResponse.headers.get('ETag') || undefined;
        setUploadProgress((prev) => ({ ...prev, [fileInfo.fieldId]: 70 }));

        return {
          fieldId: fileInfo.fieldId,
          fileName: fileInfo.fileName,
          key,
          sizeInBytes: fileInfo.file.size,
          contentType: fileInfo.contentType,
          eTag,
        };
      });

      const uploadedResults = await Promise.all(uploadPromises);

      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        Object.keys(newProgress).forEach((key) => {
          newProgress[key] = 90;
        });
        return newProgress;
      });

      const uploadPayload = {
        accountId: account.id,
        proposalId,
        creditAnalysisId: creditAnalysis?.id,
        uploadedBy: 'analyst' as const,
        files: uploadedResults,
      };

      const result = await creditAnalysisApi.uploadDocuments(uploadPayload);

      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        Object.keys(newProgress).forEach((key) => {
          newProgress[key] = 100;
        });
        return newProgress;
      });

      return result;
    },
    onSuccess: (_updatedCreditAnalysis) => {
      toast({
        title: 'Documentos enviados',
        description: 'Os documentos foram enviados com sucesso!',
      });

      // Only invalidate queries when we have valid IDs
      if (account?.id) {
        queryClient.invalidateQueries({
          queryKey: ['account-uploads', account.id, 'credit-analysis'],
        });
        queryClient.invalidateQueries({ queryKey: ['creditAnalysis', 'account', account.id] });
      }

      if (creditAnalysis?.id) {
        queryClient.invalidateQueries({
          queryKey: ['credit-analysis-document-reviews', creditAnalysis.id],
        });
        queryClient.invalidateQueries({ queryKey: ['credit-analysis', creditAnalysis.id] });
        queryClient.invalidateQueries({
          queryKey: ['credit-analysis-analysis', creditAnalysis.id],
        });
      }

      if (proposalId) {
        queryClient.invalidateQueries({ queryKey: ['creditAnalysis', 'proposal', proposalId] });
      }

      // General invalidation (safe)
      queryClient.invalidateQueries({ queryKey: ['credit-analysis'] });

      setFiles({});
      setUploadProgress({});
      setIsUploading(false);
    },
    onError: (error) => {
      toast({
        title: 'Erro',
        description:
          error instanceof Error ? error.message : 'Ocorreu um erro ao enviar os documentos.',
        variant: 'destructive',
      });
      setIsUploading(false);
      setUploadProgress({});
    },
  });

  const updateReviewMutation = useMutation({
    mutationFn: async ({
      reviewId,
      status,
      rejectionReason,
    }: {
      reviewId: string;
      status: CreditAnalysisDocumentReviewStatus;
      rejectionReason?: string;
    }) => {
      return creditAnalysisDocumentReviewApi.updateStatus({
        id: reviewId,
        status,
        rejectionReason,
      });
    },
    onSuccess: (_, variables) => {
      // Only invalidate if we have a valid creditAnalysis ID
      if (creditAnalysis?.id) {
        queryClient.invalidateQueries({
          queryKey: ['credit-analysis-document-reviews', creditAnalysis.id],
        });
      }
      const message =
        variables.status === 'approved'
          ? 'Documento aprovado com sucesso.'
          : 'Documento reprovado com sucesso.';
      toast({
        title: 'Status atualizado',
        description: message,
      });
      setApprovalDialog({ isOpen: false, fieldKey: '', fieldLabel: '', reviewId: '' });
      setRejectionDialog({
        isOpen: false,
        fieldKey: '',
        fieldLabel: '',
        reviewId: '',
        rejectionReason: '',
      });
    },
    onError: () => {
      toast({
        title: 'Erro',
        description: 'Não foi possível atualizar o status do documento.',
        variant: 'destructive',
      });
    },
  });

  const handleFileChange = async (key: string, file: File | null) => {
    if (!file) return;

    setFiles((prev) => ({ ...prev, [key]: file }));
    setIsUploading(true);

    try {
      await uploadDocumentsMutation.mutateAsync();
    } catch {
    } finally {
      setIsUploading(false);
    }
  };

  const handleViewDocument = async (fieldId: string) => {
    const upload = documentFilesMap.get(fieldId);
    if (!upload) return;

    try {
      const downloadUrl = await accountUploadApi.getDownloadUrl(upload.key);
      setPreviewFile({ url: downloadUrl, name: upload.name });
    } catch {
      console.error('Erro ao abrir documento');
    }
  };

  const handleDownloadDocument = async (fieldId: string) => {
    const upload = documentFilesMap.get(fieldId);
    if (!upload) return;

    try {
      const downloadUrl = await accountUploadApi.getDownloadUrl(upload.key);
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch {
      console.error('Erro ao baixar documento');
    }
  };

  const handleOpenApprovalDialog = (fieldKey: string, fieldLabel: string) => {
    const review = documentReviewsMap.get(fieldKey);
    if (review) {
      setApprovalDialog({
        isOpen: true,
        fieldKey,
        fieldLabel,
        reviewId: review.id,
      });
    }
  };

  const handleConfirmApproval = () => {
    updateReviewMutation.mutate({
      reviewId: approvalDialog.reviewId,
      status: 'approved',
    });
  };

  const handleOpenRejectionDialog = (fieldKey: string, fieldLabel: string) => {
    const review = documentReviewsMap.get(fieldKey);
    if (review) {
      setRejectionDialog({
        isOpen: true,
        fieldKey,
        fieldLabel,
        reviewId: review.id,
        rejectionReason: '',
      });
    }
  };

  const handleConfirmRejection = () => {
    if (!rejectionDialog.rejectionReason.trim()) {
      toast({
        title: 'Motivo obrigatório',
        description: 'Por favor, informe o motivo da reprovação.',
        variant: 'destructive',
      });
      return;
    }
    updateReviewMutation.mutate({
      reviewId: rejectionDialog.reviewId,
      status: 'rejected',
      rejectionReason: rejectionDialog.rejectionReason.trim(),
    });
  };

  const handleClosePreviewDialog = () => {
    setPreviewFile(null);
  };

  const handleCloseApprovalDialog = () => {
    setApprovalDialog({ isOpen: false, fieldKey: '', fieldLabel: '', reviewId: '' });
  };

  const handleCloseRejectionDialog = () => {
    setRejectionDialog({
      isOpen: false,
      fieldKey: '',
      fieldLabel: '',
      reviewId: '',
      rejectionReason: '',
    });
  };

  const handleRejectionReasonChange = (reason: string) => {
    setRejectionDialog((prev) => ({ ...prev, rejectionReason: reason }));
  };

  const handleOpenResendDialog = () => {
    setResendDialogOpen(true);
  };

  const handleCloseResendDialog = () => {
    setResendDialogOpen(false);
  };

  const handleResendSuccess = () => {
    setResendDialogOpen(false);
  };

  const canReviewDocuments = creditAnalysis?.status === 'IN_ANALYSIS';

  // Analyst can upload documents during analysis (IN_ANALYSIS) and all pre-analysis statuses
  // Only block when analysis is finalized (APPROVED, PARTIALLY_APPROVED, DECLINED, DOC_DECLINED, EXPIRED)
  // If creditAnalysis is null/undefined, uploads should be blocked
  const canUploadDocuments =
    creditAnalysis?.status &&
    [
      'NOT_STARTED',
      'PENDING_EXTERNAL_DOCS',
      'PENDING_ANALYSIS',
      'IN_ANALYSIS', // Analyst can add documents during analysis
    ].includes(creditAnalysis.status);

  const canDeleteDocuments =
    creditAnalysis?.status &&
    [
      'NOT_STARTED',
      'PENDING_EXTERNAL_DOCS',
      'PENDING_ANALYSIS',
      'IN_ANALYSIS', // Analyst can delete documents during analysis
    ].includes(creditAnalysis.status);

  return {
    currentFields,
    documentFilesMap,
    documentReviewsMap,
    uploadProgress,
    files,
    previewFile,
    approvalDialog,
    rejectionDialog,
    isUploading,
    canReviewDocuments,
    canUploadDocuments,
    canDeleteDocuments,
    updateReviewMutation,
    handleFileChange,
    handleViewDocument,
    handleDownloadDocument,
    handleOpenApprovalDialog,
    handleConfirmApproval,
    handleOpenRejectionDialog,
    handleConfirmRejection,
    handleClosePreviewDialog,
    handleCloseApprovalDialog,
    handleCloseRejectionDialog,
    handleRejectionReasonChange,
    resendDialogOpen,
    handleOpenResendDialog,
    handleCloseResendDialog,
    handleResendSuccess,
  };
}
